/**
 * IncrementableInput — numeric +/− input control.
 *
 * Used in the alarm control panel for custom time entry
 * (e.g. "1 hour", "30 minutes" with +/− buttons).
 */
import { memo, type FC } from "react";
import { Pressable, TextInput } from "react-native";
import "../theme"; // module augmentation for react-native-unistyles types
import { StyleSheet, useUnistyles } from "react-native-unistyles";
import { View } from "./View";
import { Text } from "./Text";

export interface IncrementableInputProps {
  readonly value: number;
  readonly onValueChange: (value: number) => void;
  readonly onAdd: () => void;
  readonly onSubtract: () => void;
  readonly maxLength?: number;
  readonly isAddDisabled?: boolean;
  readonly isSubtractDisabled?: boolean;
}

export const IncrementableInput: FC<IncrementableInputProps> = memo(
  ({ value, onValueChange, onAdd, onSubtract, maxLength = 2, isAddDisabled, isSubtractDisabled }) => {
    const { theme } = useUnistyles();

    return (
      <View flexDirection="row" justifyContent="flex-end" style={{ flexShrink: 0, flexGrow: 0 }}>
        <Pressable
          onPress={onSubtract}
          disabled={isSubtractDisabled}
          style={[
            styles.operatorButton,
            isSubtractDisabled === true ? styles.operatorButtonDisabled : undefined,
            {
              borderTopLeftRadius: theme.spacing.xl,
              borderBottomLeftRadius: theme.spacing.xl,
              borderRightWidth: 0,
            },
          ]}
        >
          <Text textStyle="lg/regular" color="text" style={{ lineHeight: 18 }}>
            -
          </Text>
        </Pressable>
        <View style={styles.inputContainer}>
          <TextInput
            value={value.toString()}
            onChangeText={(text) => {
              onValueChange(parseInt(text, 10) || 0);
            }}
            keyboardType="numeric"
            maxLength={maxLength}
            hitSlop={10}
            style={[styles.inputText, { outlineStyle: "none" } as never]}
          />
        </View>
        <Pressable
          onPress={onAdd}
          disabled={isAddDisabled}
          style={[
            styles.operatorButton,
            isAddDisabled === true ? styles.operatorButtonDisabled : undefined,
            {
              borderTopRightRadius: theme.spacing.xl,
              borderBottomRightRadius: theme.spacing.xl,
              borderLeftWidth: 0,
            },
          ]}
        >
          <Text textStyle="lg/regular" color="text" style={{ lineHeight: 18 }}>
            +
          </Text>
        </Pressable>
      </View>
    );
  },
);

const styles = StyleSheet.create((theme) => ({
  operatorButton: {
    padding: theme.spacing.xl,
    backgroundColor: "transparent",
    borderColor: theme.colors.field.border_default,
    borderWidth: 1,
    alignItems: "center",
    justifyContent: "center",
    height: 48,
    width: 44,
  },
  operatorButtonDisabled: {
    opacity: 0.5,
  },
  inputContainer: {
    width: 44,
    height: 48,
    backgroundColor: theme.colors.surface1.hover,
    borderWidth: 1,
    borderColor: theme.colors.interactive.buttons.default_background,
    overflow: "hidden",
  },
  inputText: {
    fontSize: 18,
    color: theme.colors.text,
    textAlign: "center",
    width: "100%",
    height: "100%",
  },
}));
